<script lang="ts">
  /**
   * SharedMorningView — the Club "this morning" surface: session date nav + the
   * shared-morning card, assembled from the ClubSessionSource seam.
   *
   * Plan 4: on a real native shell the source is the bridge-backed loader —
   * ALWAYS, even when it yields no sessions (that's the honest empty state,
   * never fixtures on device). The fixture crew renders only where there is no
   * native shell (dev browser, stub mode, snapshots, showcase). A wholesale
   * load failure renders DISTINCT error copy, never the empty state (no silent
   * fallbacks). showRoute is true: routeWorkoutUUID points at a real swim.
   *
   * Plan 5: an "I was there" button renders under the card when the shell
   * supports markAttendance AND the viewer isn't already in the shown roster.
   * {ok:true} re-runs the loader so the row appears from the STORE (native
   * echoed the tap into the attendance map) — never optimistic guesswork.
   * {ok:false} renders honest inline retry copy. Date-nav means a member CAN
   * tap on an old morning — deliberate offline/late tolerance (audit F12).
   */
  import { onMount } from "svelte";
  import ClubSessionHeader from "./ClubSessionHeader.svelte";
  import SharedMorningCard from "./SharedMorningCard.svelte";
  import { groupSessions, localDayKey, type ClubSession } from "../lib/clubSessions";
  import { currentSession } from "../lib/clubSessionNav";
  import { buildSharedMorning, type SharedMorningSnapshot } from "../lib/sharedMorning";
  import { selectedSessionId } from "../lib/state";
  import { fixtureClubSessionSource, type ClubSessionSource, type FixtureClubVariant } from "../lib/clubSessionSource";
  import { loadBridgeClubSessionSource, VIEWER_ID } from "../lib/bridgeClubSessionSource";
  import { bridge, hasNativeShell } from "../bridge/client";
  import { supportsMethodStore } from "../lib/nativeMethods";
  import { clubLabelFor, mergeClubSwims } from "../lib/clubCalendar";
  import type { ClubSnapshot } from "../lib/club/snapshot";

  interface Props {
    /** Browser-only fixture variant (`?clubState=…`); ignored on a native shell. */
    fixtureVariant?: FixtureClubVariant;
    onSnapshot?: (snap: SharedMorningSnapshot) => void;
    /** Roster-tap swimmer picking (item 2a) — passed through to the card. */
    pickableMemberIds?: string[];
    selectedMemberId?: string | null;
    onPickMember?: (memberId: string) => void;
    showSetStrips?: boolean;
    /** false when the host renders the date row itself (ClubTab puts it on top). */
    showHeader?: boolean;
    /** The C5 club snapshot: every clubmate swim it carries joins the sessions
     *  (clubCalendar.mergeClubSwims), so a day the calendar counts opens with
     *  every swimmer on it, months back (B-016; orchestrator ruling 2026-09-13:
     *  the day card pages the same full history as the calendar). */
    clubSnapshot?: ClubSnapshot | null;
    /** Loader only, no markup (build 13, B-027): the Club tab's unified swim card is the
     *  one surface; this still loads the sessions + firehoses and reports them upward.
     *  The card, the roster picker and "I was there" below are unwired there, not deleted. */
    headless?: boolean;
  }
  let {
    fixtureVariant = "default",
    onSnapshot,
    pickableMemberIds = [],
    selectedMemberId = null,
    onPickMember,
    showSetStrips = true,
    showHeader = true,
    clubSnapshot = null,
    headless = false,
  }: Props = $props();

  let source = $state<ClubSessionSource | null>(null);
  let loading = $state(true);
  let failed = $state(false);

  // Cancel-safe reloads (27f): every load takes a ticket; only the NEWEST
  // ticket may write `source`/`failed`, so a slow earlier load (a refresh
  // event landing while the mount load is still fetching firehoses) can never
  // overwrite a newer result. Unmount bumps the ticket so nothing lands late.
  let loadTicket = 0;

  async function loadSource() {
    const ticket = ++loadTicket;
    try {
      const next = hasNativeShell()
        ? await loadBridgeClubSessionSource()
        : fixtureClubSessionSource(fixtureVariant);
      if (ticket !== loadTicket) return;
      source = next;
      failed = false;
    } catch (e) {
      if (ticket !== loadTicket) return;
      console.warn("[club] shared-morning source failed to load", e);
      failed = true;
    } finally {
      if (ticket === loadTicket) loading = false;
    }
  }

  onMount(() => {
    void loadSource();
    // Roadmap 27f: native fires this (debounced, once per ingest batch) when a
    // companion's swim lands after launch. Re-run the loader so the new swim
    // reaches the day card from the store — no tab switch, no force-quit.
    const onRefresh = () => void loadSource();
    window.addEventListener("swimset:club-status-refresh", onRefresh);
    return () => {
      loadTicket++;
      window.removeEventListener("swimset:club-status-refresh", onRefresh);
    };
  });

  // Clubmates are already in the local snapshot. A pending HealthKit query must
  // not hold their dates/roster hostage; enrich with the full source when it lands.
  let availableSource = $derived<ClubSessionSource | null>(source ?? (clubSnapshot ? {
    swims: [], viewerId: VIEWER_ID, labelFor: (id: string) => id,
    firehoseFor: () => null, attendance: [],
  } : null));
  let sessions = $derived<ClubSession[]>(
    availableSource ? groupSessions(mergeClubSwims(availableSource.swims, availableSource.viewerId, clubSnapshot)) : [],
  );
  let shown = $derived(currentSession(sessions, $selectedSessionId));
  let morning = $derived(
    shown && availableSource
      ? buildSharedMorning(shown, availableSource.viewerId, clubLabelFor(availableSource.labelFor, clubSnapshot), availableSource.firehoseFor, availableSource.attendance, availableSource.labelsFor)
      : null,
  );

  $effect(() => {
    onSnapshot?.({ loading, failed, sessions, shown, morning, source: availableSource });
  });

  // "I was there" (Plan 5). Method-gated (no dead-end taps: browsers and older
  // shells never see it) AND hidden when the viewer already has a roster row —
  // swim or prior tap. The bridge source maps __defaultOwner__ → viewer id, so
  // this check matches after a successful tap + reload (audit F2).
  const canMarkAttendance = supportsMethodStore("markAttendance");
  let marking = $state(false);
  let markFailed = $state(false);
  let viewerInRoster = $derived(morning?.roster.some((r) => r.isViewer) ?? false);
  let showIWasThere = $derived($canMarkAttendance && morning !== null && !viewerInRoster);

  // Clear a stale failure banner on date-nav (audit MINOR): without this, tapping
  // "I was there" on morning A, failing, then navigating to morning B via
  // ClubSessionHeader still showed morning A's failure line under morning B's
  // card — same `markFailed` state, no reset on the ONLY thing that identifies
  // which morning is shown. `shown.startDate` is the session's stable identity
  // (clubSessions.groupSessions clusters by day).
  $effect(() => {
    shown?.startDate;
    markFailed = false;
  });

  async function markAttendance() {
    if (!shown || marking) return;
    marking = true;
    markFailed = false;
    try {
      const res = await bridge.markAttendance({ day: localDayKey(shown.startDate) });
      if (res.ok) {
        // Reload so the row comes from the store. Cheap: the viewer's firehoses
        // re-hit firehoseCache; companion payloads re-serialize over the local
        // bridge — fine for a small crew (audit F11; if it ever grinds, re-fetch
        // only listAttendance and rebuild the merge).
        await loadSource();
      } else {
        markFailed = true;
      }
    } catch (e) {
      console.warn("[club] markAttendance failed", e);
      markFailed = true;
    } finally {
      marking = false;
    }
  }
</script>

{#if !headless}
<section class="shared-morning" aria-label="This morning">
  {#if loading}
    <p class="empty">Loading this morning…</p>
  {:else if failed}
    <p class="empty">Couldn't load this morning. Leave the Club tab and come back to retry.</p>
  {:else if sessions.length === 0}
    <!-- VP round 2 (blockers 1 + 3): zero sessions is a dead end for the
         chevron nav (nothing to page through) and "mornings" reads as jargon
         to a first-time non-technical clubmate. One calm, plain-words line,
         no paging affordance. -->
    <p class="empty">When your club swims, those swims show up here.</p>
  {:else}
    {#if showHeader}<ClubSessionHeader {sessions} />{/if}
    {#if morning}
      <SharedMorningCard {morning} showRoute={true} {pickableMemberIds} {selectedMemberId} {onPickMember} {showSetStrips} />
      {#if showIWasThere}
        <button class="i-was-there" onclick={markAttendance} disabled={marking}>
          {marking ? "Saving…" : "I was there"}
        </button>
      {/if}
      {#if markFailed}
        <p class="mark-failed" role="status">Couldn't save — try again in a moment.</p>
      {/if}
    {:else}
      <!-- Unreachable in practice: sessions.length > 0 in this branch always
           resolves `shown` (currentSession) to a real session, so `morning`
           is never null here. Kept as an honest fallback, not dead-code bait. -->
      <p class="empty">When your club swims, those swims show up here.</p>
    {/if}
  {/if}
</section>
{/if}

<style>
  .shared-morning {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  .empty {
    color: var(--text-muted);
    font-size: 15px;
    margin: 0;
    padding: var(--space-4) 0;
    text-align: center;
  }

  /* "I was there" — one obvious full-width tappable action (jargon-free copy,
     44px+ target). Card-surface secondary style: the morning card stays the hero. */
  .i-was-there {
    width: 100%;
    min-height: 44px;
    padding: var(--space-3) var(--space-4);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    background: var(--bg-card);
    color: var(--accent-primary);
    font-size: 17px;
    font-weight: 600;
    cursor: pointer;
  }
  .i-was-there:disabled {
    opacity: 0.6;
  }
  .mark-failed {
    color: var(--text-muted);
    font-size: 15px;
    margin: 0;
    text-align: center;
  }
</style>
